import {
  useEffect,
  Ref,
  useCallback,
  FunctionComponent,
  forwardRef,
  ReactNode,
  TransitionEvent,
  useState
} from 'react';
import styled, { css } from 'styled-components';

import { useOuterEvent, useConsolidatedRef, usePrevious } from '../../hooks';
import { AsProp, BaseProps, ForwardProps } from '../../types';
import { reflow } from '../../utils';
import { defaultThemeProp } from '../../theme';

export interface DrawerProps extends AsProp, BaseProps {
  /**
   * If true, the Drawer will be visible.
   * @default false
   */
  open: boolean;
  /** Content of the Drawer. */
  children?: ReactNode;
  /**
   * Determines whether a drop shadow should be visible along the edge of the Drawer for contrast purposes.
   * @default false
   */
  shadow?: boolean;
  /**
   * Used to determine which side of the viewport the Drawer should open from.
   * @default "right"
   */
  placement?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * The position of the Drawer.
   * @default "absolute"
   */
  position?: 'absolute' | 'fixed';
  /**
   * Determines the speed of the transition. This value comes from `theme.base.animation.speed`.
   * @default "0.25s"
   */
  transitionSpeed?: string;
  /**
   * The width or height that the Drawer will open to, depending on its placement.
   * @default "100%"
   */
  size?: string;
  /**  Callback fired after Drawer opens.  */
  onAfterOpen?: () => void;
  /**  Callback fired after Drawer closes.  */
  onAfterClose?: () => void;
  /**  Callback fired before Drawer opens.  */
  onBeforeOpen?: () => void;
  /** Callback fired before Drawer closes.  */
  onBeforeClose?: () => void;
  /** Callback fired when a click action occurs outside of the Drawer.  */
  onOuterClick?: () => void;
  /**
   * If true, removes the component from the real DOM when closed. For full virtual DOM unmount(if necessary), perform at a parent level.
   * @default false
   */
  nullWhenClosed?: boolean;
}

type State = 'open' | 'closed' | 'opening' | 'closing';

export const StyledDrawer = styled.div<Partial<DrawerProps & { state: State }>>(props => {
  const { state, shadow, position, placement, transitionSpeed, size, theme } = props;
  let dimension = 'horizontal';
  let axis = 'X';
  let anchor = 'top';

  if (placement === 'top' || placement === 'bottom') {
    dimension = 'vertical';
    axis = 'Y';
    anchor = 'left';
  }

  const translate = state === 'open' || state === 'opening' ? 0 : '100%';
  const speed = transitionSpeed || theme.base.animation.speed;

  return css`
    z-index: ${theme.base['z-index'].drawer};
    position: ${position};
    ${placement}: 0;
    ${anchor}: 0;
    height: ${dimension === 'vertical' ? size : '100%'};
    width: ${dimension === 'horizontal' ? size : '100%'};
    transition-property: transform, box-shadow, opacity;
    transition-duration: max(${speed}, 0.0001s);
    transition-timing-function: ${theme.base.animation.timing.ease};
    transform: ${state === 'open'
      ? 'none'
      : `translate${axis}(${placement === 'top' || placement === 'left' ? '-' : ''}${translate})`};

    ${shadow &&
    css`
      box-shadow: ${theme.base.shadow.high};
    `}
  `;
});

StyledDrawer.defaultProps = defaultThemeProp;

const Drawer: FunctionComponent<DrawerProps & ForwardProps> = forwardRef(
  (props: DrawerProps, ref: Ref<HTMLElement>) => {
    const {
      open = false,
      shadow = false,
      position = 'absolute',
      children,
      placement = 'right',
      transitionSpeed,
      size = '100%',
      onAfterOpen,
      onAfterClose,
      onBeforeOpen,
      onBeforeClose,
      onOuterClick,
      nullWhenClosed = false,
      ...restProps
    } = props;

    const drawerRef = useConsolidatedRef(ref);

    const [state, setState] = useState<State>(open ? 'open' : 'closed');

    let prevState = usePrevious(state);
    if (!prevState) prevState = state;

    useOuterEvent(
      'mousedown',
      [drawerRef.current],
      useCallback(() => {
        if (open) onOuterClick?.();
      }, [open, onOuterClick])
    );

    useEffect(() => {
      if (open && (state === 'closed' || state === 'closing')) {
        onBeforeOpen?.();
        reflow(drawerRef.current);
        setState('opening');
      } else if (!open && (state === 'open' || state === 'opening')) {
        onBeforeClose?.();
        setState('closing');
      } else if (open && state === 'open' && prevState !== 'open') {
        onAfterOpen?.();
      } else if (!open && state === 'closed' && prevState !== 'closed') {
        onAfterClose?.();
      }
    }, [open, state, prevState, onBeforeOpen, onBeforeClose, onAfterOpen, onAfterClose]);

    const onTransitionEnd = useCallback(
      (e: TransitionEvent): void => {
        if (e.target !== drawerRef.current || e.propertyName !== 'transform') return;
        setState(open ? 'open' : 'closed');
      },
      [open]
    );

    return state === 'closed' && !open && nullWhenClosed ? null : (
      <StyledDrawer
        ref={drawerRef}
        position={position}
        shadow={shadow && open}
        transitionSpeed={transitionSpeed}
        placement={placement}
        size={size}
        open={open}
        state={state}
        onTransitionEnd={onTransitionEnd}
        {...restProps}
      >
        {children}
      </StyledDrawer>
    );
  }
);

export default Drawer;
